import { useState, useEffect, useCallback } from 'react';
import { whatsappConfigService } from '@/services/whatsappConfigService';

/**
 * Custom hook for managing WhatsApp configuration
 * @param {string} projectId - Project ID
 * @param {Object} options - Hook options
 * @param {boolean} options.enabled - Whether to fetch config on mount (default: true)
 * @returns {Object} WhatsApp configuration state and operations
 */
export const useWhatsAppConfig = (projectId, options = {}) => {
  const { enabled = true } = options;
  const [whatsappConfig, setWhatsappConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch WhatsApp configuration from the server
   */
  const fetchWhatsAppConfig = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const response = await whatsappConfigService.getWhatsAppConfig(projectId);

      if (response.success) {
        setWhatsappConfig(response.data.whatsappConfig);
      } else {
        // If no config found, set to null (not an error)
        if (response.error?.code === 'NOT_FOUND') {
          setWhatsappConfig(null);
        } else {
          setError(
            response.error?.message || 'Failed to fetch WhatsApp configuration'
          );
        }
      }
    } catch (err) {
      console.error('Error fetching WhatsApp config:', err);
      setError(err.message || 'Failed to fetch WhatsApp configuration');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /**
   * Create new WhatsApp configuration
   */
  const createWhatsAppConfig = useCallback(
    async configData => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await whatsappConfigService.createWhatsAppConfig(
          projectId,
          configData
        );

        if (response.success) {
          setWhatsappConfig(response.data.whatsappConfig);
          return response.data.whatsappConfig;
        } else {
          throw new Error(
            response.error?.message || 'Failed to create WhatsApp configuration'
          );
        }
      } catch (err) {
        console.error('Error creating WhatsApp config:', err);
        setError(err.message || 'Failed to create WhatsApp configuration');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Update existing WhatsApp configuration
   */
  const updateWhatsAppConfig = useCallback(
    async updateData => {
      if (!projectId) throw new Error('Project ID is required');

      setLoading(true);
      setError(null);

      try {
        const response = await whatsappConfigService.updateWhatsAppConfig(
          projectId,
          updateData
        );

        if (response.success) {
          setWhatsappConfig(response.data.whatsappConfig);
          return response.data.whatsappConfig;
        } else {
          throw new Error(
            response.error?.message || 'Failed to update WhatsApp configuration'
          );
        }
      } catch (err) {
        console.error('Error updating WhatsApp config:', err);
        setError(err.message || 'Failed to update WhatsApp configuration');
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [projectId]
  );

  /**
   * Delete WhatsApp configuration
   */
  const deleteWhatsAppConfig = useCallback(async () => {
    if (!projectId) throw new Error('Project ID is required');

    setLoading(true);
    setError(null);

    try {
      const response =
        await whatsappConfigService.deleteWhatsAppConfig(projectId);

      if (response.success) {
        setWhatsappConfig(null);
        return response;
      } else {
        throw new Error(
          response.error?.message || 'Failed to delete WhatsApp configuration'
        );
      }
    } catch (err) {
      console.error('Error deleting WhatsApp config:', err);
      setError(err.message || 'Failed to delete WhatsApp configuration');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  /**
   * Test WhatsApp connection
   */
  const testWhatsAppConnection = useCallback(async () => {
    if (!projectId) throw new Error('Project ID is required');

    try {
      const response =
        await whatsappConfigService.testWhatsAppConnection(projectId);

      if (response.success) {
        return response.data;
      } else {
        throw new Error(
          response.error?.message || 'Failed to test WhatsApp connection'
        );
      }
    } catch (err) {
      console.error('Error testing WhatsApp connection:', err);
      throw err;
    }
  }, [projectId]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Load WhatsApp configuration on mount and when projectId changes
   * Only fetch if enabled is true
   */
  useEffect(() => {
    if (projectId && enabled) {
      fetchWhatsAppConfig();
    }
  }, [projectId, enabled, fetchWhatsAppConfig]); // Only fetch when enabled is true

  return {
    // State
    whatsappConfig,
    loading,
    error,

    // Actions
    fetchWhatsAppConfig,
    createWhatsAppConfig,
    updateWhatsAppConfig,
    deleteWhatsAppConfig,
    testWhatsAppConnection,
    clearError,
  };
};
